/**
 * Typed API client for the Sourcr backend.
 *
 * Uses the Supabase session access_token for auth. Falls back to the
 * dev-token bypass when no session exists (local dev without Supabase).
 *
 * Set VITE_API_URL in .env.local to point at your local Worker:
 *   VITE_API_URL=http://localhost:8787
 */
import { supabase } from './supabase';
import type { SearchRecord, ChatMessage, SearchStatus, SearchCriteria, FavoriteCandidate, Candidate } from '@sourcr/shared';

const API_BASE =
  (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';

// ── Auth header ───────────────────────────────────────────────────────────────

async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token ?? 'dev-token';
  return { Authorization: `Bearer ${token}` };
}

// ── Core fetch helper ─────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await authHeader();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
      ...(init?.headers as Record<string, string> | undefined),
    },
  });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: { message: string } };
      if (body.error?.message) message = body.error.message;
    } catch { /* ignore */ }
    throw new Error(message);
  }

  const body = (await res.json()) as { data: T };
  return body.data;
}

// ── Last-search memory (survives F5 on a bare /results or /chat) ────────────────

const LAST_SEARCH_KEY = 'sourcr:last-search-id';

export function rememberSearch(id: string): void {
  try { window.localStorage.setItem(LAST_SEARCH_KEY, id); } catch { /* ignore */ }
}

export function getLastSearchId(): string | null {
  try { return window.localStorage.getItem(LAST_SEARCH_KEY); } catch { return null; }
}

// ── Searches ─────────────────────────────────────────────────────────────────

export function listSearches(): Promise<{ searches: SearchRecord[] }> {
  return apiFetch('/api/searches');
}

export function getSearch(id: string): Promise<SearchRecord> {
  return apiFetch(`/api/searches/${id}`);
}

export function createSearch(
  title: string,
  criteria: SearchCriteria,
): Promise<SearchRecord> {
  return apiFetch('/api/searches', {
    method: 'POST',
    body: JSON.stringify({ title, criteria }),
  });
}

export function deleteSearch(id: string): Promise<{ deleted: string }> {
  return apiFetch(`/api/searches/${id}`, { method: 'DELETE' });
}

// ── Messages ──────────────────────────────────────────────────────────────────

export function listMessages(searchId: string): Promise<{ messages: ChatMessage[] }> {
  return apiFetch(`/api/searches/${searchId}/messages`);
}

export interface SendMessageResult {
  message: ChatMessage;
  searchStatus: SearchStatus;
  confirmed: boolean;
  /** P1: 2–4 tappable quick-reply options for the current question (omitted otherwise). */
  suggestedAnswers?: string[];
}

export function sendMessage(
  searchId: string,
  content: string,
): Promise<SendMessageResult> {
  return apiFetch(`/api/searches/${searchId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });
}

// ── Candidates ────────────────────────────────────────────────────────────────

export function listCandidates(searchId: string): Promise<{ candidates: Candidate[]; total: number }> {
  return apiFetch(`/api/searches/${searchId}/candidates?limit=100&offset=0`);
}

// ── Favorites ─────────────────────────────────────────────────────────────────

export function listFavorites(): Promise<{ favorites: FavoriteCandidate[] }> {
  return apiFetch('/api/favorites');
}

export function addFavorite(candidateId: string): Promise<FavoriteCandidate> {
  return apiFetch('/api/favorites', {
    method: 'POST',
    body: JSON.stringify({ candidateId }),
  });
}

export function updateFavorite(
  id: string,
  update: { status?: string; notes?: string },
): Promise<FavoriteCandidate> {
  return apiFetch(`/api/favorites/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(update),
  });
}

export function removeFavorite(id: string): Promise<{ deleted: string }> {
  return apiFetch(`/api/favorites/${id}`, { method: 'DELETE' });
}

// ── User settings ─────────────────────────────────────────────────────────────

export function getUserSettings(): Promise<{ userId: string; email: string; enabledSources: string[] }> {
  return apiFetch('/api/users/me');
}

export function updateUserSettings(settings: { enabledSources: string[] }): Promise<{ enabledSources: string[] }> {
  return apiFetch('/api/users/me/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

// ── Stats ─────────────────────────────────────────────────────────────────────

export interface Stats {
  totalSearches: number;
  totalCandidates: number;
  totalFavorites: number;
  contacted: number;
}

export function getStats(): Promise<Stats> {
  return apiFetch('/api/stats');
}

// ── Search retry ──────────────────────────────────────────────────────────────

export function retrySearch(id: string): Promise<SearchRecord> {
  return apiFetch(`/api/searches/${id}/retry`, { method: 'POST' });
}

/** Start the background search after criteria are confirmed (status must be 'ready'). */
export function runSearch(id: string): Promise<SearchRecord> {
  return apiFetch(`/api/searches/${id}/run`, { method: 'POST' });
}

/** Phase 7: log a behaviour signal (fire-and-forget — never blocks the UI). */
export function submitSignal(input: {
  source: string;
  url: string;
  signal: 'opened' | 'imported' | 'skipped';
  rankShown?: number;
  roleFamily?: string;
}): void {
  try {
    void apiFetch('/api/signals', { method: 'POST', body: JSON.stringify(input) }).catch(() => {});
  } catch { /* ignore */ }
}

// ── Candidate opt-in pool (public) ──────────────────────────────────────────

export interface OptInInput {
  name: string;
  profileUrl?: string;
  headline?: string;
  location?: string;
  skills: string[];
  summary?: string;
  email?: string;
  available?: string;
  consent: true;
}

/** Public — submit your own profile with consent. Returns a deletion token. */
export function submitOptIn(input: OptInInput): Promise<{ id: string; deleteToken: string }> {
  return apiFetch('/api/pool/optin', { method: 'POST', body: JSON.stringify(input) });
}

/** Public self-service deletion ("forget me") via the token returned at opt-in. */
export function deletePoolCandidate(id: string, token: string): Promise<{ deleted: string }> {
  return apiFetch(`/api/pool/${id}?token=${encodeURIComponent(token)}`, { method: 'DELETE' });
}

// ── Candidates (paginated) ────────────────────────────────────────────────────

export function listCandidatesPaged(
  searchId: string,
  limit = 20,
  offset = 0,
): Promise<{ candidates: Candidate[]; total: number }> {
  return apiFetch(`/api/searches/${searchId}/candidates?limit=${limit}&offset=${offset}`);
}
